import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getHours, getMinutes, set } from 'date-fns';
import { Form } from '@components/ui/form';
import { useExternalScheduleForm } from '@forms/schedules/external';
import { defaultValues, getMaterialOptionsValues } from '@forms/schedules/external/constants';
import { useRemoveExternalSchedule } from '@services/schedules/external/remove';
import { ExternalScheduleWhenInputs } from '@components/schedules/dialogs/external/inputs/when';
import { ExternalScheduleContactsInputs } from '@components/schedules/dialogs/external/inputs/contacts';
import { ExternalScheduleMaterialInputs } from '@components/schedules/dialogs/external/inputs/material';
import { toLocalWallClock, toTenantDate } from '@utils/timeZone';
import { RelatedCardView } from '../view';
import { RelatedConfirmed } from '../confirmed';
import { useSettle } from '../../useSettle';
import { contactName, shiftIso } from '../../helpers';
import { RelatedTask } from '../../types';
import { RelatedFormCardProps } from '../types';

export const RelatedExternalCard = ({
  data,
  shiftDays,
  updated,
  register,
  icon,
  ...view
}: RelatedFormCardProps) => {
  const { t } = useTranslation('app', { keyPrefix: 'scheduling.phone.related' });
  const { id, startsAt, status, contact, extraComment } = data;
  const task = data.task as RelatedTask;
  const taskId = task.id;
  const jobId = task.job?.id || '';
  const isOrder = task.taskType === 'material_order';
  const material = isOrder && task.material?.options?.length ? task.material : undefined;

  const { form, onSubmit, locked } = useExternalScheduleForm({
    jobId,
    taskId,
    scheduleId: id,
    schedule: data,
    note: task.note,
    successHandler: () => save.finish({ ok: true }),
  });
  const save = useSettle(locked);

  const removal = useRemoveExternalSchedule({
    id,
    jobId,
    taskId,
    callback: (resp) => remove.finish({ ok: Boolean(resp?.success), error: 'removeFailed' }),
  });
  const remove = useSettle(removal.isLoading);

  useEffect(() => {
    const wallClock = toLocalWallClock(toTenantDate(shiftIso(startsAt, updated ? 0 : shiftDays)));
    form.reset({
      ...defaultValues,
      date: wallClock,
      time: wallClock,
      taskId,
      confirmed: status === 'approved',
      externalContactId: contact?.id || '',
      extraComment: extraComment || '',
      materialOptions: getMaterialOptionsValues(task.material?.options),
    });
  }, [startsAt, status, contact?.id, extraComment, shiftDays, updated]);

  useEffect(() => {
    register(id, {
      move: () =>
        save.run(
          async () => {
            let invalid = false;
            await form.handleSubmit(onSubmit, () => {
              invalid = true;
            })();
            return !invalid && !Object.keys(form.formState.errors).length;
          },
          { ok: false, error: 'saveFailed' },
        ),
      remove: () =>
        remove.run(
          async () => {
            removal.mutate();
            return true;
          },
          { ok: false, error: 'removeFailed' },
        ),
    });
    return () => register(id);
  });

  const date = form.watch('date');
  const time = form.watch('time');
  const to = date && time ? set(date, { hours: getHours(time), minutes: getMinutes(time) }) : date;

  return (
    <Form {...form}>
      <RelatedCardView
        {...view}
        name={task.name}
        icon={icon}
        owner={contactName(contact)}
        from={toLocalWallClock(toTenantDate(startsAt))}
        to={to}
        updated={updated}
        confirmed={<RelatedConfirmed id={id} disabled={view.disabled} />}>
        <ExternalScheduleWhenInputs disabled={view.disabled} />
        {(!isOrder || task.dateConfirmation === 'external') && (
          <ExternalScheduleContactsInputs
            data={data}
            jobId={jobId}
            initialContactId={contact?.id}
            disabled={view.disabled}
            open
            phone
          />
        )}
        {material && (
          <div className="flex flex-col gap-3">
            <p className="text-[15px] font-bold text-neutral-800">{t('materialProperties')}</p>
            <ExternalScheduleMaterialInputs material={material} editable disabled={view.disabled} />
          </div>
        )}
      </RelatedCardView>
    </Form>
  );
};
